import { cleanKeys } from "./cleanKeys"
import {
  resolveTheme,
  UISK_TextStyles,
  UISK_ThemeModes,
} from "./generatedTheme"
import {
  ContainerSize,
  DensitySize,
  RadiusSize,
  ResolvedTheme,
  SpacingSize,
  WidthSize,
} from "./types"

export type StyledComponentsTheme = {
  color: ResolvedTheme["variables"]["1. Color modes"]
  radius: { [k in RadiusSize]: string | number }
  spacing: { [k in SpacingSize]: string | number }
  density: { [k in DensitySize]: string | number }
  width: { [k in WidthSize]: string | number }
  container: { [k in ContainerSize]: string | number }
  typography: ResolvedTheme["variables"]["7. Typography"]
  textStyles: UISK_TextStyles
}

function parseTheme(theme: ResolvedTheme): StyledComponentsTheme {
  return {
    color: theme.variables["1. Color modes"],
    radius: setToUnit<"radius">(
      cleanKeys(theme.variables["2. Radius"], "radius"),
    ),
    spacing: setToUnit<"spacing">(
      cleanKeys(theme.variables["3. Spacing"], "spacing"),
    ),
    density: setToUnit<"density">(
      cleanKeys(theme.variables["4. Density"], "density"),
    ),
    width: setToUnit<"width">(cleanKeys(theme.variables["5. Widths"], "width")),
    container: setToUnit<"container">(
      cleanKeys(theme.variables["6. Containers*"], "container"),
    ),
    typography: setToUnit<"typography">(theme.variables["7. Typography"]),
    textStyles: convertFontSizeToPx(theme.textStyles),
  }
}

function convertFontSizeToPx(textStyles: ResolvedTheme["textStyles"]) {
  return Object.fromEntries(
    Object.entries(textStyles).map(([key, style]) => [
      key,
      { ...style, fontSize: style.fontSize + "px" },
    ]),
  ) as unknown as UISK_TextStyles
}

type Unit = "px"

function setToUnit<T extends keyof StyledComponentsTheme>(
  object: StyledComponentsTheme[T],
  unit: Unit = "px",
) {
  return Object.fromEntries(
    Object.entries(object).map(([key, value]) =>
      typeof value === "number" ? [key, `${value}${unit}`] : [key, value],
    ),
  ) as StyledComponentsTheme[T]
}

export const generateUISKTheme = (mode: UISK_ThemeModes["1. Color modes"]) =>
  parseTheme(resolveTheme({ "1. Color modes": mode }))
